import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './PlannerAdmin.css';

interface PlannedOrder {
  id: string;
  plan_name: string;
  customer_name: string;
  estimated_ship_date: string;
  verification_deadline: string;
  status: string;
  status_display: string;
  is_verification_overdue: boolean;
  days_until_ship: number;
  line_count: number;
  total_bags: number;
}

const PlannedOrdersAdmin: React.FC = () => {
  const [orders, setOrders] = useState<PlannedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [overdueOnly, setOverdueOnly] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [statusFilter, overdueOnly]);

  const fetchOrders = async () => {
    try {
      const params: any = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (overdueOnly) params.overdue = 'true';

      const response = await api.get('/planner/admin/orders/', { params });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendVerification = async (orderId: string) => {
    try {
      await api.post(`/planner/admin/orders/${orderId}/send_verification/`);
      alert('Verification email sent!');
      fetchOrders();
    } catch (error) {
      console.error('Failed to send verification:', error);
      alert('Failed to send verification email');
    }
  };

  const handleConvert = async (orderId: string) => {
    try {
      const response = await api.post(`/planner/admin/orders/${orderId}/convert/`);
      alert(`Order converted to Sales Order #${response.data.sales_order_id}`);
      fetchOrders();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to convert order');
    }
  };

  const getStatusClass = (order: PlannedOrder) => {
    if (order.is_verification_overdue) return 'overdue';
    return order.status;
  };

  if (loading) {
    return <div className="planner-admin-loading">Loading orders...</div>;
  }

  return (
    <div className="orders-admin-page">
      <div className="page-header">
        <h1>Planned Orders</h1>
        <Link to="/planner" className="btn-secondary">← Back to Dashboard</Link>
      </div>

      <div className="filters-bar">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="pending_verification">Pending Verification</option>
          <option value="verified">Verified</option>
          <option value="converted">Converted</option>
        </select>
        <label>
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={(e) => setOverdueOnly(e.target.checked)}
          />
          Overdue Only
        </label>
      </div>

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Plan</th>
              <th>Ship Date</th>
              <th>Deadline</th>
              <th>Status</th>
              <th>Items</th>
              <th>Total Bags</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(order => (
              <tr key={order.id}>
                <td>{order.customer_name}</td>
                <td>{order.plan_name}</td>
                <td>
                  {order.estimated_ship_date}
                  {order.days_until_ship !== null && (
                    <small style={{ color: '#666', display: 'block' }}>
                      {order.days_until_ship > 0
                        ? `${order.days_until_ship} days`
                        : order.days_until_ship === 0
                          ? 'Today'
                          : 'Past due'}
                    </small>
                  )}
                </td>
                <td>{order.verification_deadline}</td>
                <td>
                  <span className={`status-badge ${getStatusClass(order)}`}>
                    {order.is_verification_overdue ? 'OVERDUE' : order.status_display}
                  </span>
                </td>
                <td>{order.line_count}</td>
                <td>{order.total_bags}</td>
                <td className="actions-cell">
                  <Link to={`/planner/orders/${order.id}`} className="btn-small view">
                    View
                  </Link>
                  {order.status === 'draft' && (
                    <button
                      className="btn-small edit"
                      onClick={() => handleSendVerification(order.id)}
                    >
                      Send
                    </button>
                  )}
                  {order.status === 'verified' && (
                    <button
                      className="btn-small edit"
                      onClick={() => handleConvert(order.id)}
                    >
                      Convert
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {orders.length === 0 && (
          <p style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            No orders found matching your filters
          </p>
        )}
      </div>
    </div>
  );
};

export default PlannedOrdersAdmin;
